import { useCallback, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { SlotStates } from "../../api/models/ScheduleSlotModel";
import ScheduleCalendar from "../../components/calendar/ScheduleCalendar";
import DropDown from "../../components/forms/dropdown/DropDown";
import useCalendarViewDate from "../../hooks/useCalendarViewDate";
import { useTelawah } from "../../hooks/useTelawah";
import CalendarScheduleModel from "../../models/CalendarScheduleModel";

// TODO: Remove the following mock data
import AppButton from "../../components/buttons/AppButton";
import { indexRecitationSchedule } from "../../api/endpoints/candidate/recitationScheduleSlots";
import { makeApiRequest } from "../../helpers";
import { ApiResponse } from "../../api/apiResponse";
import CalendarScheduleSlotModel from "../../models/CalendarScheduleSlotModel";
import ApiDateParser from "../../helpers/parsers/apiDateParser";
import ConfirmationModal from "../../components/modal/ConfirmationModal";
import { useAuth } from "../../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { indexStudentFormatSchedules } from "../../api/endpoints/students/schedules";
import SchedulingService from "../../services/schedulingService";
import { changeScheduleRequests } from "../../api/endpoints/students/requests";
import { ToastContext } from "../../App";
import { useAvailabilitySchedule } from "../../hooks/useAvailabilitySchedule";
import CalendarScheduleCandidate from "../../api/models/CalendarScheduleCandidate";
import CalendarScheduleRequest from "../../api/models/CalendarScheduleRequest";
import CalendarScheduleModelRequest from "../../models/CalendarScheduleModelRequest";
import TimeZoneAlert from "../../components/alerts/timeZone";

export default function ChangeAppointments() {
  const LocalBasePath = "page.candidate.schedule";
  const { t, i18n } = useTranslation("", { keyPrefix: LocalBasePath });
  const authContext = useAuth();
  const navigate = useNavigate();
  const [availabilitySchedule, refetchAvailabilitySchedule] =
    useAvailabilitySchedule();
  const { telawahOptions = [] } = useTelawah();
  const [selectedTelawahId, setSelectedTelawah] = useState<
    number | string | null | undefined
  >(authContext?.currentUser?.telawah_id);
  const { viewingDate, onChangeViewingDate } = useCalendarViewDate();
  const [schedule, setSchedule] = useState<object>({});
  const { currentUser }: any = useAuth();
  const [selectedSlots, setSelectedSlots] = useState<
    CalendarScheduleSlotModel[]
  >([]);
  const [, updateState] = useState<Object>();
  const toast = useContext(ToastContext);

  const forceUpdate = useCallback(() => updateState({}), []);
  const [isDone, setIsDone] = useState(false);
  const [selectedSessions, setSelectedSessions] =
    useState<CalendarScheduleModel | null>(null);
  const getUserSchedule: any = async () => {
    const UserScheduleApi = await indexStudentFormatSchedules();
    if (UserScheduleApi.isSuccess) {
      return UserScheduleApi.data;
    }
  };

  useEffect(() => {
    fetchAndSetSchedule();
  }, []);

  const fetchAndSetSchedule = async () => {
    let fetchedSchedule = await getUserSchedule();
    setSchedule(fetchedSchedule);
  };
  const mergeSchedules = () => {
    const calendarAvailabilitySchedule = new CalendarScheduleModelRequest(
      availabilitySchedule,
      SlotStates.available
    );
    let calendarRecitationSchedule = new CalendarScheduleModelRequest(schedule);
    calendarRecitationSchedule.slots = calendarRecitationSchedule.slots.filter(
      (slot) => !slot.isCandidateSlot
    );
    calendarRecitationSchedule.modifySlotsStateByDate(viewingDate);

    /**
     * The order of spreading schedules is important as we may have duplication or different slots with the same start and end time
     * The last added slots will override the first slots in the UI if there is duplication
     * So, we give here the higher priority to the recitation schedule
     */
    let calendarSchedule = new CalendarScheduleModelRequest({});
    calendarSchedule.slots = [
      ...calendarAvailabilitySchedule.slots,
      ...calendarRecitationSchedule.slots,
    ];
    setSelectedSessions(calendarSchedule);
  };

  const onSlotClick = (slot: any) => {
    let finalSelected: any = [];

    let schedulingService = new SchedulingService(
      selectedSessions!,
      currentUser
    );
    schedulingService.toggleSlot(slot);
    setSelectedSessions(schedulingService.schedule);
    finalSelected = schedulingService.schedule.slots;

    let selected: any = finalSelected.map((slot: any) => {
      if (slot?.state === 2) return slot;
    });
    setSelectedSlots(selected);
    forceUpdate();
  };

  const handleSubmit = async () => {
    const newSlots: any = [];
    selectedSlots.forEach((slot) => {
      if (slot != null) newSlots.push(new CalendarScheduleRequest(slot));
    });
    const response: ApiResponse = await changeScheduleRequests({
      schedule_slots_attributes: newSlots,
    });
    if (response.isSuccess) {
      setIsDone(true);
    } else {
      response.errorMessages.forEach((errorMessage) => {
        toast?.error(errorMessage);
      });
      setIsDone(false);
    }
  };

  const handleCloseModal = () => {
    setIsDone(false);
    navigate("/student/dashboard/appointments-requests");
  };
  useEffect(() => {
    if (!!schedule) mergeSchedules();
  }, [schedule]);
  return (
    <div className="flex flex-col w-screen h-screen overflow-y-auto divide-y divide-gray-200">
      <div className="flex flex-col justify-between gap-5 p-6 pb-4">
        <h2 className="text-lg font-semibold leading-8 text-gray-900">
          {t("changeSchedule")}
        </h2>
        <div className="my-0.5">
          <TimeZoneAlert />
        </div>
        <DropDown
          name={t("TelawahFilter")}
          setValue={(val: any) => setSelectedTelawah(val)}
          defaultValue={`${selectedTelawahId}`}
          label={t("telawah")}
          placeholder={t("chooseTelawah")}
          error={false}
          options={telawahOptions}
          className="w-full"
          disabled
        />
        <p className="text-xs font-medium text-gray-400">{t("disclaimer")}</p>
      </div>
      <ScheduleCalendar
        selectedDate={viewingDate}
        viewingDate={viewingDate}
        schedule={selectedSessions}
        onChangeViewingDate={onChangeViewingDate}
        onSlotClick={onSlotClick}
        compact={true}
        showDates={false}
        highlightSelectedDate={false}
      />
      {!!selectedSlots.length && (
        <div className="flex justify-end flex-shrink-0 gap-4 px-6 py-4">
          <AppButton buttonShape="primary" onClick={handleSubmit}>
            {t("send")}
          </AppButton>
        </div>
      )}
      <ConfirmationModal
        btnText="requestPreview"
        isOpen={isDone}
        onClose={handleCloseModal}
        email={authContext?.currentUser?.email || ""}
      />
    </div>
  );
}
